/**
 * Enhanced Table Configuration Models
 * Supports both simple and complex table layouts
 */

// ============================================
// COLUMN CONFIGURATION
// ============================================

export type ColumnAlign = 'left' | 'center' | 'right';
export type ColumnType = 'text' | 'number' | 'currency' | 'percentage' | 'date' | 'action' | 'template' | 'rowspan' | 'nested';

export interface ColumnConfig {
  key: string;
  label: string;
  type?: ColumnType;
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  align?: ColumnAlign;
  sticky?: boolean;
  sortable?: boolean;
  filterable?: boolean;
  editable?: boolean;
  hidden?: boolean;
  format?: string;              // Format string for numbers/dates
  tooltip?: string;
  cssClass?: string;
  headerCssClass?: string;
  template?: string;            // Template name for ng-template
  children?: ColumnConfig[];    // For grouped/nested headers
  rowspan?: number;             // For complex layouts
  colspan?: number;             // For complex layouts
  valueGetter?: (row: any, column: ColumnConfig) => any;
  valueFormatter?: (value: any, row: any, column: ColumnConfig) => string;
  cellRenderer?: string;        // Custom cell renderer name
}

// ============================================
// TABLE CONFIGURATION
// ============================================

/**
 * Base configuration for configurable tables
 * Named differently to avoid conflict with common-table's TableConfig
 */
export interface ConfigurableTableConfig {
  // Appearance
  height?: string;
  maxHeight?: string;
  minHeight?: string;
  striped?: boolean;
  hover?: boolean;
  bordered?: boolean;
  compact?: boolean;
  responsive?: boolean;

  // Header
  stickyHeader?: boolean;
  showHeader?: boolean;
  headerHeight?: string;

  // Rows
  rowHeight?: string;
  alternateRowColors?: boolean;
  highlightOnHover?: boolean;

  // Features
  pagination?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  sorting?: boolean;
  filtering?: boolean;
  selection?: boolean;
  multiSelect?: boolean;

  // Empty state
  emptyMessage?: string;
  emptyIcon?: string;

  // Loading
  loading?: boolean;
  loadingMessage?: string;

  // Styling
  tableClass?: string;
  headerClass?: string;
  bodyClass?: string;
  rowClass?: string | ((row: any, index: number) => string);
}

// ============================================
// COMPLEX TABLE LAYOUT
// ============================================

/**
 * Header group for multi-level headers
 */
export interface HeaderGroup {
  label: string;
  colspan?: number;
  rowspan?: number;
  align?: ColumnAlign;
  cssClass?: string;
  children?: HeaderGroup[];
}

/**
 * Row group for grouped rows (like the transportation table)
 */
export interface RowGroup {
  groupKey: string;
  groupLabel: string;
  expanded?: boolean;
  rows: any[];
}

/**
 * Cell span configuration for complex layouts
 */
export interface CellSpan {
  rowIndex: number;
  colKey: string;
  rowspan?: number;
  colspan?: number;
}

/**
 * Complex table configuration
 */
export interface ComplexTableConfig extends ConfigurableTableConfig {
  // Multi-level headers
  headerGroups?: HeaderGroup[][];

  // Row grouping
  rowGroups?: RowGroup[];
  groupByKey?: string;

  // Cell spanning
  cellSpans?: CellSpan[];

  // Row header (first column as header)
  rowHeaderKey?: string;

  // Footer
  showFooter?: boolean;
  footerData?: any[];

  // Caption
  caption?: string;
  captionPosition?: 'top' | 'bottom';
}

// ============================================
// TABLE EVENTS
// ============================================

export interface TableCellClickEvent {
  row: any;
  column: ColumnConfig;
  rowIndex: number;
  value: any;
}

export interface TableRowClickEvent {
  row: any;
  rowIndex: number;
}

export interface TableSortEvent {
  column: ColumnConfig;
  direction: 'asc' | 'desc' | null;
}

export interface TablePageEvent {
  pageIndex: number;
  pageSize: number;
  totalItems: number;
}

export interface TableSelectionEvent {
  selected: any[];
  row?: any;
  checked?: boolean;
}

export interface TableEditEvent {
  row: any;
  column: ColumnConfig;
  oldValue: any;
  newValue: any;
}

// ============================================
// TABLE DATA HELPERS
// ============================================

/**
 * Transform flat data for grouped display
 */
export function groupDataBy<T>(data: T[], key: keyof T): RowGroup[] {
  const groups = new Map<string, T[]>();

  data.forEach(item => {
    const groupKey = String(item[key]);
    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }
    groups.get(groupKey)!.push(item);
  });

  return Array.from(groups.entries()).map(([groupKey, rows]) => ({
    groupKey,
    groupLabel: groupKey,
    expanded: true,
    rows,
  }));
}

/**
 * Calculate rowspans for first column grouping
 */
export function calculateRowSpans(data: any[], groupKey: string): Map<number, number> {
  const spans = new Map<number, number>();
  let currentGroup = '';
  let startIndex = 0;
  let count = 0;

  data.forEach((item, index) => {
    const value = item[groupKey];

    if (value !== currentGroup) {
      if (count > 0) {
        spans.set(startIndex, count);
      }
      currentGroup = value;
      startIndex = index;
      count = 1;
    } else {
      count++;
    }
  });

  // Handle last group
  if (count > 0) {
    spans.set(startIndex, count);
  }

  return spans;
}

/**
 * Flatten nested data for table display
 */
export function flattenNestedData(data: any[], childKey: string, parentKey?: string): any[] {
  const result: any[] = [];

  const flatten = (items: any[], parent?: any) => {
    items.forEach(item => {
      const flatItem = { ...item };
      if (parent && parentKey) {
        flatItem[parentKey] = parent;
      }
      result.push(flatItem);

      if (item[childKey] && Array.isArray(item[childKey])) {
        flatten(item[childKey], item);
      }
    });
  };

  flatten(data);
  return result;
}

// ============================================
// PREDEFINED TABLE CONFIGS
// ============================================

export const DEFAULT_TABLE_CONFIG: ConfigurableTableConfig = {
  striped: true,
  hover: true,
  bordered: true,
  stickyHeader: true,
  responsive: true,
  showHeader: true,
  emptyMessage: 'No data available',
  loading: false,
};

export const SIMPLE_TABLE_CONFIG: ConfigurableTableConfig = {
  ...DEFAULT_TABLE_CONFIG,
  compact: false,
  alternateRowColors: true,
};

export const COMPLEX_TABLE_CONFIG: ComplexTableConfig = {
  ...DEFAULT_TABLE_CONFIG,
  showFooter: false,
  captionPosition: 'bottom',
};

export const REPORT_TABLE_CONFIG: ConfigurableTableConfig = {
  striped: false,
  hover: false,
  bordered: true,
  stickyHeader: false,
  responsive: true,
  showHeader: true,
  compact: true,
  tableClass: 'report-table',
};
